import { TRAFFIC_CONTROLS, TRAFFIC_STATUS_TEXTS, isAnomalyText } from '../texts.js';
import { formatDayMonth, toRome } from '../time.js';
import type { ComputedTrafficEvent, DomainSnapshot, IsoDateTime, TrafficStatus } from '../types.js';
import { instantMs } from './ledger.js';

/**
 * Current level at `tIso`: the most recent event with occurredAt ≤ t, the highest id winning a tie
 * (workbook Cruscotto!M4:M5, the last row); none = VERDE (docs/05 §6). The scope of the event does
 * not matter, as in the workbook.
 */
export function trafficLevelAt(snap: DomainSnapshot, tIso: IsoDateTime): TrafficStatus {
  const t = instantMs(tIso);
  let best: { ms: number; id: number; status: TrafficStatus } | null = null;
  for (const e of snap.trafficEvents) {
    const ms = instantMs(e.occurredAt);
    if (!(ms <= t)) continue;
    if (best && (ms < best.ms || (ms === best.ms && e.id < best.id))) continue;
    best = {
      ms,
      id: e.id,
      status: { level: e.level, since: e.occurredAt, eventId: e.id, scopePointId: e.scopePointId },
    };
  }
  return best?.status ?? { level: 'VERDE', since: null, eventId: null, scopePointId: null };
}

/** Caption under the current level (Cruscotto!K8): "dal 28/10 ore 15:50" or "nessuna variazione registrata". */
export function trafficSinceText(status: TrafficStatus): string {
  if (!status.since) return TRAFFIC_STATUS_TEXTS.noChanges;
  const rome = toRome(status.since);
  return TRAFFIC_STATUS_TEXTS.since(formatDayMonth(rome.date), rome.time);
}

/**
 * Control text of every traffic event (docs/05 §6, Registro_Semaforo!M). As in the workbook every
 * event needs the confirmation of all the (active) points, whatever its scope.
 */
export function trafficControls(snap: DomainSnapshot): ComputedTrafficEvent[] {
  const activePoints = new Set(snap.points.filter((p) => p.active).map((p) => p.id));
  return snap.trafficEvents.map((e) => {
    const confirmedCount = new Set(
      e.confirmations
        .filter((c) => c.confirmed && activePoints.has(c.pointId))
        .map((c) => c.pointId),
    ).size;
    const allConfirmed = confirmedCount >= activePoints.size;
    let control: string;
    if (Number.isNaN(instantMs(e.occurredAt))) control = TRAFFIC_CONTROLS.missingDateTime;
    else if (e.level === 'VERDE') {
      control = allConfirmed ? TRAFFIC_CONTROLS.ok : TRAFFIC_CONTROLS.resumeAwaitingConfirmations;
    } else if (!allConfirmed) control = TRAFFIC_CONTROLS.missingClosureConfirmations;
    else if (!e.cardsSecured) control = TRAFFIC_CONTROLS.cardsToSecure;
    else control = TRAFFIC_CONTROLS.ok;
    return { ...e, confirmedCount, control, isAnomaly: isAnomalyText(control) };
  });
}
